import { API_BASE_URL } from './apiBaseUrl';

/**
 * 학습 결과(시뮬) 페이지 디버그 — URL·params·본문(절삭)을 `console`에 남김, Authorization 는 마스킹.
 *
 * 켜는 법(하나면 됨):
 * 1) `my-app/.env` 또는 `.env.local` — `REACT_APP_DEBUG_STUDY_RESULT_API=true` 후 **개발 서버 재시작** (필수)
 * 2) (개발만) 콘솔: `localStorage.setItem('debugStudyResultApi','1'); location.reload()`
 */
const LS_KEY = 'debugStudyResultApi';

function flagFromProcessEnv(): boolean {
	if (typeof process === 'undefined') return false;
	const v = String(process.env.REACT_APP_DEBUG_STUDY_RESULT_API || '')
		.trim()
		.toLowerCase();
	return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

function flagFromLocalStorage(): boolean {
	if (process.env.NODE_ENV === 'production') return false;
	if (typeof window === 'undefined' || !window.localStorage) return false;
	try {
		const v = String(window.localStorage.getItem(LS_KEY) || '')
			.trim()
			.toLowerCase();
		return v === '1' || v === 'true';
	} catch {
		return false;
	}
}

export function isStudyResultDevLogEnabled(): boolean {
	return flagFromProcessEnv() || flagFromLocalStorage();
}

function maskBearer(authHeader: string | undefined): string {
	if (authHeader == null || authHeader === '') return '(none)';
	if (!authHeader.toLowerCase().startsWith('bearer ')) return '(non-bearer set)';
	const raw = authHeader.slice(7).trim();
	if (raw.length <= 16) return 'Bearer ***';
	return `Bearer ${raw.slice(0, 6)}…${raw.slice(-4)}`;
}

function truncateForLog(value: unknown, max = 6_000): string {
	try {
		const s = JSON.stringify(value);
		if (s.length <= max) return s;
		return `${s.slice(0, max)}… [truncated ${s.length} chars]`;
	} catch {
		return String(value);
	}
}

type HeaderRecord = Record<string, string | undefined> | undefined;

/**
 * BFF/플랫폼 중 어디에나 — 요청·응답을 한 턴에 묶어서 출력
 */
export function logStudyResultApiTurn(
	phase: 'req' | 'res' | 'err',
	scope: string,
	payload: {
		method?: string;
		url: string;
		headers?: HeaderRecord;
		params?: Record<string, string | number | boolean | undefined>;
		status?: number;
		/** `res` — 응답 본문, `err` — 실패 시 응답/에러 본문(있다면) */
		body?: unknown;
		err?: unknown;
	}
): void {
	if (!isStudyResultDevLogEnabled()) return;
	const h = payload.headers;
	const headersSafe: Record<string, string> | undefined = h
		? (() => {
				const o: Record<string, string> = { ...h } as Record<string, string>;
				if (o['Authorization'] != null) o['Authorization'] = maskBearer(o['Authorization']);
				if (o['authorization'] != null) o['Authorization'] = maskBearer(o['authorization']);
				delete o['authorization'];
				return o;
			})()
		: undefined;

	const line: Record<string, unknown> = {
		scope,
		phase,
		...(payload.method && { method: payload.method }),
		url: payload.url,
		...(Object.keys(payload.params || {}).length > 0 && { params: payload.params }),
		...(headersSafe && { headers: headersSafe }),
		...(payload.status != null && { status: payload.status }),
		...(payload.body !== undefined && phase === 'res' && { bodyPreview: truncateForLog(payload.body, 8_000) }),
		...(payload.body !== undefined && phase === 'err' && { bodyPreview: truncateForLog(payload.body, 2_000) }),
		...(payload.err != null && { err: String(payload.err) }),
	};

	if (phase === 'err') {
		console.error('[StudyResult][API]', line);
	} else {
		console.log('[StudyResult][API]', line);
	}
}

export function buildRelativeApiUrl(
	path: string,
	params?: Record<string, string | number | boolean | null | undefined>
): string {
	const p = path.startsWith('/') ? path : `/${path}`;
	if (!params || !Object.keys(params).length) return p;
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v === undefined || v === null || v === '') continue;
		sp.set(k, typeof v === 'boolean' ? String(v) : String(v));
	}
	const q = sp.toString();
	return q ? `${p}?${q}` : p;
}

/** `REACT_APP_API_BASE_URL` 이 비어 있으면 현재 origin(예: localhost:3000) + path 로 표시(프록시 동일 오리진) */
export function displayBffApiUrl(pathWithQuery: string): string {
	const p = pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`;
	if (API_BASE_URL) {
		return `${API_BASE_URL.replace(/\/$/, '')}${p}`;
	}
	if (typeof window !== 'undefined' && window.location?.origin) {
		return `${window.location.origin}${p}`;
	}
	return p;
}

/**
 * 시뮬레이션 결과 페이지 `useSimulationResultsApi` — **개발**에서만 BFF·api2 URL과 요청/응답 요약을 한 번에 출력.
 * (`REACT_APP_DEBUG_STUDY_RESULT_API` 없이도 SimulationResults용 추적이 보이게 함. 프로덕션은 출력 안 함.)
 */
export function logSimulationResultsApiSession(payload: {
	classId: string;
	scenarioId: number;
	bff: {
		scenarioInfo?: {
			method: string;
			url: string;
			request: { language?: string } | null;
			response: unknown | null;
			/** BFF가 null(실패)로 돌아온 경우 */
			nullableNote?: string | null;
		};
		classLabInfo?: {
			method: string;
			url: string;
			request: null;
			response: unknown | null;
			matchedContent: unknown | null;
			mongoObjectId: string | null;
		};
		playRecords?: {
			method: string;
			url: string;
			request: Record<string, string | number | boolean | undefined>;
			response: unknown | null;
			matchedRecord: unknown | null;
			mongoScenarioPlayId: string | null;
		};
	};
	platform: {
		adminLab: { method: string; fullUrl: string; request: Record<string, string | number | boolean | undefined>; response: unknown | null } | null;
		practice: {
			method: string;
			fullUrl: string;
			path: string;
			skipped: boolean;
			skipReason?: string;
			response: unknown | null;
		} | null;
	};
}): void {
	if (process.env.NODE_ENV === 'production' || typeof console === 'undefined' || !console.log) {
		return;
	}
	const trim = (v: unknown) => {
		return truncateForLog(v, 4_000);
	};

	console.log('[SimulationResults][BFF·백엔드(호출 주소)]', {
		웹_BFF: { 
			...(payload.bff.scenarioInfo ? { 시나리오정보_GET: payload.bff.scenarioInfo.url } : {}),
			...(payload.bff.classLabInfo ? { 클래스Lab정보_GET: payload.bff.classLabInfo.url } : {}),
			...(payload.bff.playRecords ? { 플레이기록_GET: payload.bff.playRecords.url } : {})
		},
		플랫폼_api2: {
			...(payload.platform.adminLab ? { 관리자_랩_GET: payload.platform.adminLab.fullUrl } : {}),
			...(payload.platform.practice
				? {
						실습_데이터_GET: payload.platform.practice.skipped ? '(조회 생략)' : payload.platform.practice.fullUrl,
					}
				: {}),
		},
	});
	console.log('[SimulationResults][Request 본문·쿼리]', {
		...(payload.bff.scenarioInfo ? { 시나리오_info: { method: payload.bff.scenarioInfo.method, url: payload.bff.scenarioInfo.url, ...((payload.bff.scenarioInfo.request || {}) as object) } } : {}),
		...(payload.bff.classLabInfo ? { 클래스Lab_info: { method: payload.bff.classLabInfo.method, url: payload.bff.classLabInfo.url, mongoObjectId: payload.bff.classLabInfo.mongoObjectId } } : {}),
		...(payload.bff.playRecords ? { 플레이_기록: { method: payload.bff.playRecords.method, url: payload.bff.playRecords.url, mongoScenarioPlayId: payload.bff.playRecords.mongoScenarioPlayId, ...payload.bff.playRecords.request } } : {}),
		...(payload.platform.adminLab
			? { 관리자_랩: { method: payload.platform.adminLab.method, url: payload.platform.adminLab.fullUrl, ...payload.platform.adminLab.request } }
			: {}),
		...(payload.platform.practice
			? { 실습_데이터: payload.platform.practice.skipped ? { skipped: true, reason: payload.platform.practice.skipReason } : { method: 'GET', path: payload.platform.practice.path, fullUrl: payload.platform.practice.fullUrl } }
			: {}),
	});
	const infoNote = payload.bff.scenarioInfo?.nullableNote;
	console.log('[SimulationResults][Response 본문]', {
		classId: payload.classId,
		scenarioId: payload.scenarioId,
		...(payload.bff.scenarioInfo ? { 시나리오_info: infoNote ? { note: infoNote, body: trim(payload.bff.scenarioInfo.response) } : { body: trim(payload.bff.scenarioInfo.response) } } : {}),
		...(payload.bff.classLabInfo ? { 클래스Lab_info: { response: trim(payload.bff.classLabInfo.response), matchedContent: trim(payload.bff.classLabInfo.matchedContent), mongoObjectId: payload.bff.classLabInfo.mongoObjectId } } : {}),
		...(payload.bff.playRecords ? { 플레이_기록: { response: trim(payload.bff.playRecords.response), matchedRecord: trim(payload.bff.playRecords.matchedRecord), mongoScenarioPlayId: payload.bff.playRecords.mongoScenarioPlayId } } : {}),
		...(payload.platform.adminLab ? { 관리자_랩: { body: trim(payload.platform.adminLab.response) } } : {}),
		...(payload.platform.practice && !payload.platform.practice.skipped
			? { 실습_데이터: { body: trim(payload.platform.practice.response) } }
			: { 실습_데이터: { skipped: true, body: null } }),
	});
}
